/**
 * Single FAQ entry
 */

//  Deps
import { createComponent } from "react-fela";
import { useEffect, useRef, useState } from "react";
import { textStyles } from "~/styleguide/typography";
import { fluid } from "~/styleguide/utils";
import { motion } from "framer-motion";
import { fancyList } from "~/styleguide/fancy-list";

const focusableSelector = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]"
].join(",");

export default function FAQItem({ item, index }) {
    const { question, answer } = item;
    const [open, setOpen] = useState(false);
    const answerRef = useRef(null);
    const questionId = `faq-question-${index}`;
    const answerId = `faq-answer-${index}`;

    useEffect(() => {
        if (!answerRef.current) return;

        const focusableElements = answerRef.current.querySelectorAll(
            focusableSelector
        );

        if (!open && answerRef.current.contains(document.activeElement)) {
            const questionButton = document.getElementById(questionId);
            if (questionButton) questionButton.focus();
        }

        focusableElements.forEach(element => {
            if (!open) {
                if (!element.hasAttribute("data-original-tabindex")) {
                    element.setAttribute(
                        "data-original-tabindex",
                        element.getAttribute("tabindex") || ""
                    );
                }

                element.setAttribute("tabindex", "-1");
                return;
            }

            const originalTabIndex = element.getAttribute(
                "data-original-tabindex"
            );

            if (originalTabIndex === "") {
                element.removeAttribute("tabindex");
            } else if (originalTabIndex !== null) {
                element.setAttribute("tabindex", originalTabIndex);
            }

            element.removeAttribute("data-original-tabindex");
        });
    }, [answer, open, questionId]);

    const handleClick = () => {
        setOpen(!open);
    };

    return (
        <Container>
            <Content>
                <Question
                    aria-controls={answerId}
                    aria-expanded={open}
                    id={questionId}
                    onClick={handleClick}
                    open={open}
                    type="button"
                    extend={textStyles.faqOrFeature}
                >
                    <span itemProp="name">{question}</span>
                </Question>

                <Answer>
                    <motion.section
                        id={answerId}
                        itemProp="text"
                        initial={false}
                        animate={open ? "open" : "collapsed"}
                        variants={{
                            open: { opacity: 1, height: "auto" },
                            collapsed: { opacity: 0, height: 0 }
                        }}
                        transition={{
                            duration: 0.3,
                            ease: [0.04, 0.62, 0.23, 0.98]
                        }}
                        style={{ overflow: "hidden" }}
                        aria-hidden={!open}
                        ref={answerRef}
                    >
                        {answer && (
                            <AnswerInner
                                dangerouslySetInnerHTML={{ __html: answer }}
                            />
                        )}
                    </motion.section>
                </Answer>
            </Content>
        </Container>
    );
}

const Container = createComponent(() => ({
    marginBottom: 12,
    ":last-of-type": {
        marginBottom: 0
    }
}));

const Question = createComponent(({ open }) => ({
    appearance: "none",
    background: "transparent",
    border: 0,
    color: "inherit",
    cursor: "pointer",
    display: "block",
    font: "inherit",
    marginBottom: 0,
    padding: 0,
    position: "relative",
    textAlign: "left",
    width: "100%",
    ":after": {
        width: 15,
        height: 15,
        content: "''",
        border: "solid black",
        borderWidth: "0 3px 3px 0",
        display: "inline-block",
        padding: 3,
        position: "absolute",
        right: 0,
        top: -7,
        bottom: 0,
        margin: "auto",
        transform: "rotate(45deg)",
        transition: "all 250ms"
    },
    extend: [
        fluid("paddingRight", { min: 40, max: 175 }),
        {
            condition: open,
            style: {
                ":after": {
                    transform: "rotate(-135deg)",
                    top: 7
                }
            }
        }
    ]
  }),
  "button",
  ["aria-controls", "aria-expanded", "id", "itemprop", "itemscope", "itemtype", "onClick", "type"]

);

const Content = createComponent(fancyList, "div");

const Answer = createComponent(() => ({}),
  "div",
);

const AnswerInner = createComponent(
    () => ({
        paddingTop: 18,
        extend: [fluid("paddingRight", { min: 40, max: 175 })]
    }),
    "div",
    ["dangerouslySetInnerHTML"]
);
